/**
 * El sello de visita — ADR-020.
 *
 * REGLA DURA, y es la que gobierna todo este archivo: **el sello es un badge
 * positivo y NUNCA una marca negativa sobre quien no lo tiene.** Si no hay
 * sello, este componente no pinta nada: `null`, no un hueco gris, no "sin
 * verificar", no un asterisco.
 *
 * La razón es de producto, no estética. Publicar sin haber estado en el lugar
 * es legítimo desde ADR-020: alguien que comió el viernes y escribe el domingo
 * no hizo nada malo. Dibujar su ausencia de sello lo convertiría en ciudadano
 * de segunda, y ADR-029 ya tuvo cuidado de que no lo sea (el sello multiplica
 * el XP, pero el techo diario es el mismo con o sin él).
 *
 * `docs/09` §8.2 regla 2 lo dijo antes que nadie, para otro contexto: la
 * reputación modula el número, no la voz.
 */

interface SelloProps {
  /** Si es `false`, no se renderiza nada. */
  presente: boolean;
  /** `pill` para la ficha; `punto` para el avatar en listas. */
  forma?: 'pill' | 'punto';
}

export function Sello({ presente, forma = 'pill' }: SelloProps) {
  if (!presente) return null;

  if (forma === 'punto') {
    return (
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: 'var(--gu-success)',
          border: '1.5px solid var(--gu-surface-raised)',
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        borderRadius: 'var(--gu-radius-full, 999px)',
        background: 'var(--gu-success-surface)',
        color: 'var(--gu-success-text)',
        fontSize: 'var(--gu-text-caption)',
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {/*
       * El punto es decorativo y lo dice; el texto es el que informa. Un badge
       * de color sin texto no es información accesible.
       */}
      <span aria-hidden="true" style={{ fontSize: '0.9em' }}>
        ●
      </span>
      Estuvo ahí
    </span>
  );
}

/**
 * El agregado de la ficha: cuántas voces llevan sello.
 *
 * Se dice en positivo y con los dos números a la vista —"4 de 7 son de gente
 * que estuvo ahí"— porque informa sin acusar a las otras tres. Un porcentaje
 * haría lo contrario: convertiría el resto en el complemento de algo bueno.
 *
 * Con cero selladas NO se dice nada. "0 de 3 estuvieron ahí" es exactamente la
 * marca negativa que la regla de arriba prohíbe.
 */
export function ResumenDeSellos({ total, selladas }: { total: number; selladas: number }) {
  if (selladas === 0) return null;

  return (
    <span style={{ color: 'var(--gu-text-secondary)' }}>
      {selladas === total
        ? total === 1
          ? 'De alguien que estuvo ahí'
          : `Las ${total} son de gente que estuvo ahí`
        : `${selladas} de ${total} son de gente que estuvo ahí`}
    </span>
  );
}
